import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../../config/config');

const toLocalDatetimeInput = (d) => {
    const date = d ? new Date(d) : new Date();
    const tzOffset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - tzOffset * 60 * 1000);
    return local.toISOString().slice(0, 16);
};

const AddDriverTimelineNote = ({ open, onClose, driverId, onSaved }) => {
    const [form, setForm] = useState({
        driver_id: driverId || '',
        event_type: 'note',
        title: '',
        description: '',
        event_date: toLocalDatetimeInput(new Date()),
        created_by: ''
    });

    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { user } = useAuth();

    useEffect(() => {
        setForm(prev => ({
            ...prev,
            driver_id: driverId || prev.driver_id,
        }));

        const storedUser = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage.getItem('username') : null;
        const createdByName = user?.name || storedUser;
        if (createdByName) {
            setForm(prev => ({ ...prev, created_by: createdByName }));
        } else {
            setForm(prev => ({ ...prev, created_by: prev.created_by || 'system' }));
        }

        // Reset errors when opening
        if (open) {
            setErrors({});
            setError('');
            setForm(prev => ({ ...prev, event_date: prev.event_date || toLocalDatetimeInput(new Date()) }));
        }
    }, [driverId, open, user]);

    if (!open) return null;

    const validate = () => {
        const newErrors = {};
        if (!form.driver_id) newErrors.driver_id = 'Driver ID is required';
        if (!form.event_type) newErrors.event_type = 'Event type is required';
        if (!form.title) newErrors.title = 'Title is required';
        if (!form.created_by) newErrors.created_by = 'Created by is required';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setError('Please fix the highlighted errors before saving.');
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                driver_id: form.driver_id,
                event_date: form.event_date ? new Date(form.event_date).toISOString() : new Date().toISOString(),
                event_type: form.event_type,
                title: form.title,
                description: form.description,
                created_by: form.created_by
            };

            const endpoint = `${rootUrl.replace(/\/$/, '')}/drivers/timeline`;
            const token = (typeof window !== 'undefined' && (localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('auth_token'))) || '';
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || data.error || 'Failed to save timeline note');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error(err);
            setError(err.message || 'An error occurred while saving.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                <div className="bg-gradient-to-r from-[#FF5733] to-[#FF8C66] px-6 py-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-white">Add Timeline Note</h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">&times;</button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-3 text-red-700 text-sm rounded">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                            <select
                                name="event_type"
                                value={form.event_type}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none transition-all ${errors.event_type ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            >
                                <option value="note">General Note</option>
                                <option value="warning">Warning/Infraction</option>
                                <option value="incident">Incident/Accident</option>
                                <option value="hiring">Hiring/Onboarding</option>
                                <option value="termination">Termination/Offboarding</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                            <input
                                type="datetime-local"
                                name="event_date"
                                value={form.event_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            placeholder="Brief summary of the event"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none transition-all ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        />
                        {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Detailed description..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none transition-all resize-none"
                        ></textarea>
                    </div>

                    <div className="flex justify-end space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-[#FF5733] hover:bg-[#E64A19] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 flex items-center"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                    Saving...
                                </>
                            ) : 'Save Note'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddDriverTimelineNote;
